import Loading from 'components/Loading';
import { useCallback, useState, useRef, useEffect } from 'react';
import {
    Alert,
    Form,
    FormInput,
    ListGroup,
    ListGroupItem,
    ListGroupItemHeading,
} from 'shards-react';
import useFetch from 'utils/hooks/useFetch';

const AllowedReferences = ({
    approved,
    references = [],
    recRef = [],
    withCategory,
    service = '',
    addReference = () => null,
}) => {
    const [value, setValue] = useState(() => '');
    const { status, data, fetchData } = useFetch({
        method: 'POST',
        url: '/references/get-references',
        immediate: false,
    });

    const [listHeight, setListHeight] = useState(0);
    const refListElement = useRef(null);

    const updateValue = useCallback(
        (e) => {
            const startsWith = e?.target?.value ?? '';

            setValue(() => startsWith);

            if (startsWith === '') return null;

            return fetchData({
                data: { service, existing: references, withCategory, startsWith },
            });
        },
        [service, fetchData, references, withCategory]
    );

    const returnValue = useCallback(
        (e) => {
            e.preventDefault();
            if (value === '') return null;
            const result = data?.[0];
            if (!result) return null;
            addReference(result);
            return setValue(() => '');
        },
        [value, data, addReference]
    );

    const formWidth = useCallback(() => {
        const viewPort = window.innerWidth;
        if (viewPort <= 1000) {
            return 14;
        } else if (viewPort <= 1280) {
            return 28
        } else {
            return 34;
        }
    }, []);

    useEffect(() => {
        if (refListElement.current)
            setListHeight(refListElement.current.clientHeight);
    }, [data, references]);

    if (approved) return null;

    return (
        <div className='mr-3' style={{ minWidth: `${formWidth()}rem` }}>
            <Form onSubmit={returnValue}>
                <FormInput
                    data-test='ref-notmanual-input'
                    type='text'
                    placeholder='Neue Referenz einfügen'
                    title='Leerzeichen ist nicht erlaubt!'
                    pattern='^\S+$'
                    onChange={updateValue}
                    value={value}
                />
            </Form>

            {status === 'fetching' && (
                <div className='position-absolute' style={{ zIndex: 3 }}>
                    <Loading />
                </div>
            )}

            {value && status === 'fetched' && (
                <ListGroup
                    className='position-absolute shadow mt-2'
                    style={{
                        zIndex: 3,
                        background: 'white',
                        width: `${formWidth()}rem`,
                    }}
                >
                    <ListGroupItemHeading>Erlaubte Referenzen:</ListGroupItemHeading>
                    <Alert
                        theme='danger'
                        className='font-weight-bold'
                        open={!data?.length}
                    >
                        Keine Referenzen gefunden!
                    </Alert>

                    <div
                        ref={refListElement}
                        className={`bg-white w-100 p-0 rounded ${
                            listHeight >= 450 &&
                            'overflow-auto'
                        }`}
                        style={{
                            minHeight: 50,
                            maxHeight: 450,
                        }}
                    >
                        {data
                            ?.filter(
                                (ref) =>
                                    !references.some(
                                        (selected) => selected?._id === ref?._id
                                    )
                            )
                            ?.filter(
                                (ref) =>
                                    !recRef.some(
                                        (recognised) => recognised?._id === ref?._id
                                    )
                            )
                            .map((ref) => {
                                const { _id, reference, category } = ref;
                                return (
                                    <ListGroupItem
                                        key={_id}
                                        data-test={`optionRef-${reference}`}
                                        style={{ cursor: 'pointer' }}
                                        className='p-2'
                                        onClick={() => addReference(ref)}
                                    >
                                        {reference}
                                        <span>{category && ` - ${category}`}</span>
                                    </ListGroupItem>
                                );
                            })}
                    </div>
                    <div className='d-flex justify-content-center py-2'>
                        <button
                            data-test='ref-notmanual-close-btn'
                            className='btn btn-success'
                            onClick={() => setValue('')}
                        >
                            Schließen
                        </button>
                    </div>
                </ListGroup>
            )}
        </div>
    );
};

export default AllowedReferences;
